import { type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useRecoilValue } from 'recoil'

import { itemAtomFamily } from '@/atoms/_editorAtoms'
import { caseImagesAtom, caseImageVariantsAtom } from '@/atoms/caseImageAtoms'
import { Tooltip } from '@/components/global/Tooltip/Tooltip'
import { Icon } from '@/components/priority/Icon/Icon'
import { ToolbarIconButton } from '@/components/page/Items/ItemControlOverlay/ItemControlOverlay.styled'
import { useAiImageEditsEnabled, useSharedEnhance } from '@/hooks/caseImages'
import {
  enhancementActionForItem,
  type EditedWithAiItem,
} from '@/utils/editor/editedWithAi'
import { ITEM_ENHANCEMENT_TEST_IDS } from './canvasEnhancementTestIds'

type CanvasEnhancementControlProps = {
  /** The selected canvas image item whose toolbar draws the control. */
  itemId: string
}

/**
 * The enhancement control in a selected canvas photo's toolbar, the
 * editor's way to undo an enhancement where it was made: "Revert changes"
 * while the item shows an accepted enhancement (it carries the "Edited with
 * AI" tag), and "Show enhancement" while its photo is reverted. Both run the
 * editor's shared flow (`EditorEnhanceProvider`): Revert opens the same
 * confirmation as the Case Images grid's Revert, in the editor's modal, and
 * Show enhancement saves at once, free, like the grid's. Either changes the
 * photo for everyone, and the canvas follows. Neither bills anything: the
 * enhancement stays accepted, and billed once, either way.
 *
 * Drawn only where the change moves this item (`enhancementActionForItem`),
 * inside the editor, and while AI image edits are on
 * (`useAiImageEditsEnabled`), since the backend refuses both while they are
 * off. The toolbar holding it is hidden on an item whose controls this
 * person may not use (an asset-locked item for a non-admin).
 */
export const CanvasEnhancementControl = ({
  itemId,
}: CanvasEnhancementControlProps) => {
  const { t } = useTranslation()
  const item = useRecoilValue(itemAtomFamily(itemId)) as
    | EditedWithAiItem
    | undefined
  const caseImages = useRecoilValue(caseImagesAtom)
  const variantsById = useRecoilValue(caseImageVariantsAtom)
  const shared = useSharedEnhance()
  const aiImageEditsEnabled = useAiImageEditsEnabled()

  if (!aiImageEditsEnabled || !shared) return null
  const action = enhancementActionForItem(caseImages, variantsById, item)
  if (!action) return null

  const revert = action.kind === 'revert'
  const label = revert
    ? t('caseImages.tile.revert')
    : t('caseImages.enhance.showEnhancement')

  const onClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (revert) void shared.revertCaseImage(action.caseImageId)
    else void shared.showCaseImageEnhancement(action.caseImageId)
  }

  return (
    <Tooltip text={label}>
      <ToolbarIconButton
        type="button"
        aria-label={label}
        data-testid={
          revert
            ? ITEM_ENHANCEMENT_TEST_IDS.REVERT
            : ITEM_ENHANCEMENT_TEST_IDS.SHOW_ENHANCEMENT
        }
        onClick={onClick}
      >
        <Icon
          icon={revert ? 'undoArrowIcon' : 'sparkles'}
          width="100%"
          height="100%"
        />
      </ToolbarIconButton>
    </Tooltip>
  )
}
